import type { Response } from 'lib/request/types';
import type { FailedApiResponse } from 'components/well-page-content/types';

export const extractApiErrorMessage = <T>(
  response: Response<T, FailedApiResponse>,
  fallback: string
): string =>
  (response.type === 'error' && response.data?.errors?.[0]?.message) ||
  fallback;

/**
 * Compute the next available workspace name given the names already in use.
 *
 * Mirrors the server-side per-entity de-dupe in
 * `InterestCertification::WorkspaceCreator#dedupe_name!` exactly: if `base`
 * is free it is returned as-is; otherwise the first free `` `${base} (${n})` ``
 * suffix starting at n=2 is returned. Gaps are reused — if `Foo (2)` is absent
 * it is chosen even when `Foo (3)` exists. Matching is case-sensitive and exact
 * on both `base` and the `` `${base} (n)` `` forms.
 *
 * @param  {string}   base          The desired workspace name.
 * @param  {string[]} existingNames The workspace names already taken (for this entity).
 * @return {string} The base name if free, else the first free numbered suffix.
 */
export const nextAvailableWorkspaceName = (
  base: string,
  existingNames: string[]
): string => {
  const taken = new Set(existingNames);
  if (!taken.has(base)) return base;

  let n = 2;
  while (taken.has(`${base} (${n})`)) n += 1;
  return `${base} (${n})`;
};
